import { Events } from './events';
import { Splat } from './splat';

const registerPlySequenceEvents = (events: Events) => {
    let sequenceFiles: File[] = [];
    let sequenceSplat: Splat = null;
    let sequenceFrame = -1;
    let sequenceLoading = false;
    let nextFrame = -1;
    let loadingPromise: Promise<void> | null = null;
    let sequenceVersion = 0;

    const setFrames = (files: File[]) => {
        // eslint-disable-next-line regexp/no-super-linear-backtracking
        const regex = /(.*?)(\d+)(?:\.compressed)?\.ply$/;

        // sort frames by trailing number, if it exists
        const sorter = (a: File, b: File) => {
            const avalue = a.name?.toLowerCase().match(regex)?.[2];
            const bvalue = b.name?.toLowerCase().match(regex)?.[2];
            return (avalue && bvalue) ? parseInt(avalue, 10) - parseInt(bvalue, 10) : 0;
        };

        sequenceFiles = files.slice();
        sequenceFiles.sort(sorter);
        sequenceVersion++;
        sequenceFrame = -1;
        nextFrame = -1;
        events.fire('timeline.setFrames', sequenceFiles.length);
    };

    // wait for the next render to complete
    const waitForRender = () => {
        return new Promise<void>((resolve) => {
            const off = events.on('postrender', () => {
                off.off();
                resolve();
            });
        });
    };

    const loadFrame = async (frame: number, version = sequenceVersion): Promise<Splat | null> => {
        if (frame < 0 || frame >= sequenceFiles.length) {
            return null;
        }

        if (version !== sequenceVersion) {
            return null;
        }

        if (sequenceLoading) {
            nextFrame = frame;
            if (loadingPromise) {
                await loadingPromise;
            }

            if (version !== sequenceVersion || frame === sequenceFrame) {
                return null;
            }

            return await loadFrame(frame, version);
        }

        if (frame === sequenceFrame) {
            return null;
        }

        // if user changed the scene, confirm
        if (events.invoke('scene.dirty')) {
            const result = await events.invoke('showPopup', {
                type: 'yesno',
                header: 'RESET SCENE',
                message: 'You have unsaved changes. Are you sure you want to reset the scene?'
            });

            if (result.action !== 'yes') {
                return null;
            }

            events.fire('scene.clear');
            sequenceSplat = null;
        }

        let newSplatResult: Splat | null = null;

        loadingPromise = (async () => {
            sequenceLoading = true;

            const file = sequenceFiles[frame];
            const newSplat = await events.invoke('import', [{
                filename: file.name,
                contents: file
            }], true) as Splat[];

            // wait for the new splat to render before destroying the old one
            // (forceRender is already set by updateState during import)
            await waitForRender();

            if (version !== sequenceVersion) {
                newSplat[0]?.destroy?.();
                return;
            }

            if (sequenceSplat) {
                sequenceSplat.destroy();
            }

            sequenceFrame = frame;
            sequenceSplat = newSplat[0];
            newSplatResult = newSplat[0] ?? null;
        })();

        try {
            await loadingPromise;
        } finally {
            sequenceLoading = false;
            loadingPromise = null;
        }

        if (nextFrame !== -1 && nextFrame !== sequenceFrame && version === sequenceVersion) {
            const pendingFrame = nextFrame;
            nextFrame = -1;
            await loadFrame(pendingFrame, version);
        }

        if (version !== sequenceVersion || sequenceFrame !== frame) {
            return null;
        }

        return newSplatResult ?? sequenceSplat;
    };

    const setFrame = async (frame: number) => {
        await loadFrame(frame);
    };

    events.on('plysequence.setFrames', (files: File[]) => {
        setFrames(files);
    });

    events.on('timeline.frame', async (frame: number) => {
        await setFrame(frame);
    });

    // Async function for video rendering to await PLY sequence frame loading
    // Returns the newly loaded splat if a new frame was loaded, null otherwise
    events.function('plysequence.setFrameAsync', async (frame: number): Promise<Splat | null> => {
        return await loadFrame(frame);
    });
};

export { registerPlySequenceEvents };
